// Per-category lane chart above the event stream (DESIGN_SPEC §10.9, RESTYLE §2).
//   <LaneChart points counts active? phases domain now? className? />
//     points: { t (ms from T0), c } · one 1.5px tick per event, deduplicated to one per CSS pixel per lane
//     counts: the legend's numbers · active: categories the filter keeps (the others dim)
//     phases: BOOT / RUN / COLLECT lines with HTML labels (beyond 85% a label hangs left) · domain: { from, to } in ms
//     now: the live now line (live cyan)
//   Labels are HTML (tinted category chips with counts, an axis every 1/2/5/10… s); they drop under 720px. Hovering
//   the plot reads the counts in that pixel column as a tooltip; the data is always in the stream below it.
//   laneDomain(points, phases, end?) → { from, to }
import { useLayoutEffect, useMemo, useRef, useState, type PointerEvent } from "react";
import { CATEGORIES, CategoryChip } from "@/components/ui";
import { count, rel } from "@/lib/format";
import type { Category } from "@/lib/derive/types";

export type LanePoint = { t: number; c: Category };
type LanePhase = { label: string; t: number };

const LANE = 20;
const TICK_H = 14;
const FILL: Record<Category, string> = {
  process: "fill-cat-process",
  file: "fill-cat-file",
  registry: "fill-cat-registry",
  network: "fill-cat-network",
  memory: "fill-cat-memory",
  detection: "fill-cat-detection",
  system: "fill-cat-system",
};
const STEPS_S = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600];

export function laneDomain(points: readonly LanePoint[], phases: readonly LanePhase[], end?: number): { from: number; to: number } {
  let from = 0;
  let to = end ?? 0;
  for (const p of points) {
    if (p.t < from) from = p.t;
    if (p.t > to) to = p.t;
  }
  for (const p of phases) {
    if (p.t < from) from = p.t;
    if (p.t > to) to = p.t;
  }
  const pad = Math.max(200, (to - from) * 0.015);
  return { from: from - pad, to: to + pad };
}

/** "+5" (seconds) while the run is short; "+1:30" once it spans minutes. */
function tickLabel(t: number, stepS: number, spanS: number): string {
  if (Math.abs(t) < 1) return "T0";
  const sign = t < 0 ? "−" : "+";
  const s = Math.round(Math.abs(t) / 1000);
  if (stepS < 60 && spanS <= 120) return `${sign}${s}`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${sign}${h}:${String(m).padStart(2, "0")}:${ss}` : `${sign}${m}:${ss}`;
}

/** Index of the first value ≥ x in a sorted array. */
function lowerBound(a: readonly number[], x: number): number {
  let lo = 0;
  let hi = a.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (a[mid] < x) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

export function LaneChart({
  points,
  counts,
  active,
  phases,
  domain,
  now,
  className,
}: {
  points: readonly LanePoint[];
  counts: Readonly<Record<Category, number>>;
  active?: ReadonlySet<Category>;
  phases: readonly LanePhase[];
  domain: { from: number; to: number };
  now?: number;
  className?: string;
}) {
  const plot = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  const [hover, setHover] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = plot.current;
    if (!el) return;
    setW(Math.round(el.getBoundingClientRect().width));
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const span = Math.max(1, domain.to - domain.from);
  const pct = (t: number) => ((t - domain.from) / span) * 100;

  const byCat = useMemo(() => {
    const m = Object.fromEntries(CATEGORIES.map((c) => [c, [] as number[]])) as Record<Category, number[]>;
    for (const p of points) m[p.c].push(p.t);
    for (const c of CATEGORIES) m[c].sort((a, b) => a - b);
    return m;
  }, [points]);

  const ticks = useMemo(() => {
    if (!w) return [] as { c: Category; xs: number[] }[];
    return CATEGORIES.map((c) => {
      const xs = new Set<number>();
      for (const t of byCat[c]) xs.add(Math.round(((t - domain.from) / span) * w));
      return { c, xs: [...xs] };
    });
  }, [byCat, w, domain.from, span]);

  const axis = useMemo(() => {
    const maxTicks = Math.max(3, Math.floor((w || 600) / 72));
    const stepS = STEPS_S.find((s) => span / (s * 1000) <= maxTicks) ?? STEPS_S[STEPS_S.length - 1];
    const step = stepS * 1000;
    const out: number[] = [];
    for (let t = Math.ceil(domain.from / step) * step; t <= domain.to; t += step) out.push(t);
    return { stepS, ticks: out };
  }, [w, span, domain.from, domain.to]);

  const H = CATEGORIES.length * LANE;
  const total = points.length;
  const tip = useMemo(() => {
    if (hover === null || !w) return null;
    const t = domain.from + (hover / w) * span;
    const dt = (3 / w) * span;
    const rows = CATEGORIES.map((c) => [c, lowerBound(byCat[c], t + dt) - lowerBound(byCat[c], t - dt)] as const).filter(([, n]) => n > 0);
    return { t, rows };
  }, [hover, w, span, domain.from, byCat]);

  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - r.left;
    setHover(x >= 0 && x <= r.width ? x : null);
  };

  const label = `Events over time by category: ${CATEGORIES.map((c) => `${c} ${counts[c]}`).join(", ")}`;

  return (
    <div className={`lanes ${className ?? ""}`}>
      <div className="lane-grid">
        <div />
        <div className="lane-phase" aria-hidden="true">
          {phases.map((p) => (
            <span key={p.label} className={pct(p.t) > 85 ? "end" : undefined} style={{ left: `${pct(p.t)}%` }}>
              {p.label}
            </span>
          ))}
        </div>
        <div className="lane-labs [grid-auto-rows:20px]">
          {CATEGORIES.map((c) => (
            <span key={c} className={`lane-lab ${active && !active.has(c) ? "opacity-45" : ""}`}>
              <CategoryChip c={c} size="sm" />
              <span className="n">{count(counts[c])}</span>
            </span>
          ))}
        </div>
        <div ref={plot} className="lane-plot touch-pan-y" onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
          <svg className="lane-svg" style={{ height: H }} width={w || undefined} height={H} viewBox={`0 0 ${w || 1} ${H}`} role="img" aria-label={label}>
            {w > 0 && (
              <>
                {phases.map((p) => (
                  <rect key={p.label} x={Math.round((pct(p.t) / 100) * w) - 0.5} y={0} width={1} height={H} className="fill-rule-strong" />
                ))}
                {ticks.map(({ c, xs }, li) => (
                  <g key={c} className={`${FILL[c]} ${active && !active.has(c) ? "opacity-25" : ""}`}>
                    {xs.map((x) => (
                      <rect key={x} x={x - 0.75} y={li * LANE + (LANE - TICK_H) / 2} width={1.5} height={TICK_H} />
                    ))}
                  </g>
                ))}
                {now !== undefined && <rect x={Math.round((pct(now) / 100) * w) - 0.5} y={0} width={1} height={H} className="fill-live" />}
                {hover !== null && <rect x={Math.round(hover) - 0.5} y={0} width={1} height={H} className="fill-ink-faint" />}
              </>
            )}
          </svg>
          {tip && (
            <div className="tip" role="tooltip" style={{ left: Math.min(Math.max(0, (hover ?? 0) + 12), Math.max(0, w - 220)), top: -6 }}>
              <span className="v-lead">{rel(tip.t, 1)}</span>
              <span className="tip-text">{tip.rows.length ? tip.rows.map(([c, n]) => `${c} ${n}`).join(" · ") : "no events here"}</span>
            </div>
          )}
          <div className="lane-axis" aria-hidden="true">
            {axis.ticks.map((t) => (
              <span key={t} style={{ left: `${pct(t)}%` }}>
                {tickLabel(t, axis.stepS, span / 1000)}
              </span>
            ))}
          </div>
        </div>
      </div>
      <p className="sr-only">
        {count(total)} events plotted from {rel(domain.from, 1)} to {rel(domain.to, 1)}.
      </p>
    </div>
  );
}
